/* eslint-disable no-magic-numbers */
import { CELL_WIDTH } from '../../../core/constants';

export interface DataGridLayout {
  columnWidth: number;
  contentWidth: number;
  fitsInViewport: boolean;
  isScrollable: boolean;
}

/**
 * Calculates the equal-width columns and content width for a fit-or-scroll data grid.
 *
 * The caller must provide the actual rendered column count instead of assuming a fixed 12-column forecast window.
 * We can choose any date to be budget start date. If forecast from date for budget is any day except first day of month,
 * we will have 13 columns in the grid; for example, from 2023-05-21 to 2024-05-20 will result in grid with columns
 * from MAY 23 to MAY 24.
 *
 * @param columnCount Number of rendered data columns, including financial-year columns.
 * @param viewportWidth Measured width available to the grid columns.
 * @param minimumColumnWidth Minimum usable width for one data column.
 */
export const calculateDataGridLayout = (
  columnCount: number,
  viewportWidth: number,
  minimumColumnWidth = CELL_WIDTH
): DataGridLayout => {
  // The provider can briefly receive zero dimensions while the grid is mounting, so keep all inputs non-negative.
  const safeColumnCount = Math.max(0, columnCount);
  const safeViewportWidth = Math.max(0, viewportWidth);
  const safeMinimumColumnWidth = Math.max(0, minimumColumnWidth);

  const minimumContentWidth = safeColumnCount * safeMinimumColumnWidth;
  const hasValidMeasurement = safeColumnCount > 0 && safeViewportWidth > 0;

  if (!hasValidMeasurement) {
    // Until the viewport has been measured, retain the fixed-width layout so columns remain usable and scrollable.
    return {
      columnWidth: safeMinimumColumnWidth,
      contentWidth: minimumContentWidth,
      fitsInViewport: false,
      isScrollable: safeColumnCount > 0
    };
  }

  // Expand columns to fill the viewport when possible; otherwise retain their minimum width and allow scrolling.
  const fitsInViewport = minimumContentWidth <= safeViewportWidth;
  const contentWidth = Math.max(minimumContentWidth, safeViewportWidth);

  return {
    columnWidth: contentWidth / safeColumnCount,
    contentWidth,
    fitsInViewport,
    isScrollable: !fitsInViewport
  };
};
